import { Braces, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { errorMessage, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { useEffectiveSettings, useSaveSettings, type SettingsDocuments, type SettingsKind } from "../api/queries";
import { Alert, Button, Card, ChipsInput, Field, NumberInput, PageHead, Select, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { titleCase } from "../lib/format";

type Docs = SettingsDocuments;
type Tab = "roof" | "module" | "offer" | "area" | "advanced";

const KINDS: SettingsKind[] = ["setbacks", "layout", "roof_ai", "equipment", "production", "pricing", "incentives", "financial", "brand", "service_area"];
const MATERIALS: S["RoofMaterial"][] = [
  "asphalt_shingle", "metal_standing_seam", "metal_exposed_fastener", "metal", "concrete_tile", "clay_tile", "flat_tile", "barrel_tile",
  "slate", "wood_shake", "sbs_torch_on", "epdm", "tpo", "built_up", "rubber_shingle", "lightweight_tile", "metal_interlocking", "other",
];

/** A local, editable copy of one settings kind with dirty tracking and save/discard. */
function useDraft<K extends SettingsKind>(orgId: string, docs: Docs | undefined, kind: K) {
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const saved = docs?.[kind] as Docs[K] | undefined;
  const [draft, setDraft] = useState<Docs[K] | null>(null);
  useEffect(() => {
    if (saved) setDraft(structuredClone(saved));
  }, [saved]);
  const dirty = useMemo(() => !!draft && JSON.stringify(draft) !== JSON.stringify(saved), [draft, saved]);
  const actions = (canManage: boolean) => (
    <>
      <Button size="sm" variant="ghost" icon={<RotateCcw />} disabled={!dirty} onClick={() => saved && setDraft(structuredClone(saved))}>
        Discard
      </Button>
      <Button
        size="sm"
        variant="primary"
        icon={<Save />}
        disabled={!dirty || !canManage}
        loading={save.isPending}
        onClick={() =>
          draft &&
          save.mutate(
            { kind, document: draft },
            { onSuccess: () => toast(`${titleCase(kind)} saved`), onError: (e) => toast(errorMessage(e), true) },
          )
        }
      >
        Save
      </Button>
    </>
  );
  return { draft, setDraft: setDraft as (d: Docs[K]) => void, dirty, actions };
}

function Section({ title, description, footer, children }: { title: string; description?: string; footer?: ReactNode; children: ReactNode }) {
  return <Card title={title} description={description} footer={footer}>{children}</Card>;
}

// ------------------------------------------------------------------- roof
function RoofTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const setbacks = useDraft(orgId, docs, "setbacks");
  const roof = useDraft(orgId, docs, "roof_ai");
  const s = setbacks.draft;
  const r = roof.draft;
  if (!s || !r) return null;
  const edge = (key: keyof S["SetbackEdges"], label: string) => (
    <Field label={label}>
      <NumberInput value={s.edges[key]} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, edges: { ...s.edges, [key]: v ?? 0 } })} />
    </Field>
  );
  return (
    <div className="stack">
      <Section
        title="Setbacks"
        description="Clearance kept free of modules along each roof edge (fire code). Measured in the roof plane."
        footer={setbacks.actions(canManage)}
      >
        <div className="stack">
          <div className="form-grid-3">
            {edge("eave", "Eave")}
            {edge("ridge", "Ridge")}
            {edge("rake", "Rake (gable edge)")}
            {edge("hip", "Hip")}
            {edge("valley", "Valley")}
            {edge("step_flashing", "Step flashing (wall)")}
          </div>
          <div className="form-grid-3">
            <Field label="Around obstructions">
              <NumberInput value={s.obstruction_m} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, obstruction_m: v ?? 0 })} />
            </Field>
            <Field label="Fire pathways" hint="Ridge-to-eave lanes per face">
              <NumberInput value={s.pathways.count} min={0} step={1} onChange={(v) => setbacks.setDraft({ ...s, pathways: { ...s.pathways, count: Math.round(v ?? 0) } })} />
            </Field>
            <Field label="Pathway width">
              <NumberInput value={s.pathways.width_m} min={0} step={0.05} suffix="m" onChange={(v) => setbacks.setDraft({ ...s, pathways: { ...s.pathways, width_m: v ?? 0 } })} />
            </Field>
          </div>
          {s.overrides.length > 0 && (
            <p className="hint">
              {s.overrides.length} conditional override{s.overrides.length === 1 ? "" : "s"} ({s.overrides.map((o) => o.name).join(", ")}) — edit them under Advanced.
            </p>
          )}
        </div>
      </Section>
      <Section title="Roof defaults" description="What the AI cannot see from the sky. Used for pricing when the customer does not say." footer={roof.actions(canManage)}>
        <div className="form-grid">
          <Field label="Default roof material">
            <Select
              value={r.roof_defaults.material ?? "asphalt_shingle"}
              onChange={(v) => roof.setDraft({ ...r, roof_defaults: { ...r.roof_defaults, material: v } })}
              options={MATERIALS.map((m) => ({ value: m, label: titleCase(m) }))}
            />
          </Field>
          <Field label="Storeys">
            <NumberInput value={r.roof_defaults.storeys ?? null} min={1} max={20} step={1} onChange={(v) => roof.setDraft({ ...r, roof_defaults: { ...r.roof_defaults, storeys: v == null ? undefined : Math.round(v) } })} />
          </Field>
        </div>
      </Section>
    </div>
  );
}

// ----------------------------------------------------------------- module
function ModuleTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const layout = useDraft(orgId, docs, "layout");
  const l = layout.draft;
  if (!l) return null;
  const m = l.module;
  const f = l.flat_roof;
  const setModule = (patch: Partial<S["ModuleSpec"]>) => layout.setDraft({ ...l, module: { ...m, ...patch } });
  const setFlat = (patch: Partial<S["FlatRoofSettings"]>) => layout.setDraft({ ...l, flat_roof: { ...f, ...patch } });
  return (
    <div className="stack">
      <Section title="Solar module" description="The panel every design uses." footer={layout.actions(canManage)}>
        <div className="stack">
          <div className="form-grid">
            <Field label="Model" className="span-2">
              <TextInput value={m.model} onChange={(e) => setModule({ model: e.target.value })} />
            </Field>
            <Field label="Power">
              <NumberInput value={m.watts} min={1} step={5} suffix="W" onChange={(v) => setModule({ watts: v ?? m.watts })} />
            </Field>
            <Field label="Preferred orientation">
              <Select
                value={l.orientation}
                onChange={(v) => layout.setDraft({ ...l, orientation: v })}
                options={[
                  { value: "best", label: "Best fit" },
                  { value: "portrait", label: "Portrait" },
                  { value: "landscape", label: "Landscape" },
                ]}
              />
            </Field>
            <Field label="Width (short side)">
              <NumberInput value={m.width_m} min={0.1} step={0.001} suffix="m" onChange={(v) => setModule({ width_m: v ?? m.width_m })} />
            </Field>
            <Field label="Height (long side)">
              <NumberInput value={m.height_m} min={0.1} step={0.001} suffix="m" onChange={(v) => setModule({ height_m: v ?? m.height_m })} />
            </Field>
            <Field label="Gap between modules">
              <NumberInput value={l.gap_m} min={0} step={0.01} suffix="m" onChange={(v) => layout.setDraft({ ...l, gap_m: v ?? 0 })} />
            </Field>
            <Field label="Minimum modules per face">
              <NumberInput value={l.min_modules_per_face} min={1} step={1} onChange={(v) => layout.setDraft({ ...l, min_modules_per_face: Math.max(1, Math.round(v ?? 1)) })} />
            </Field>
            <Field label="Minimum solar access" hint="Positions below this share of the best one are skipped.">
              <NumberInput value={Math.round(l.min_solar_access * 100)} min={0} max={100} step={1} suffix="%" onChange={(v) => layout.setDraft({ ...l, min_solar_access: (v ?? 0) / 100 })} />
            </Field>
            <Field label="Steepest roof">
              <NumberInput value={l.max_pitch_deg} min={0} max={90} step={1} suffix="°" onChange={(v) => layout.setDraft({ ...l, max_pitch_deg: v ?? 0 })} />
            </Field>
          </div>
        </div>
      </Section>
      <Section title="Flat roofs" description="Faces flatter than the threshold get tilted racking in rows." footer={layout.actions(canManage)}>
        <div className="form-grid-3">
          <Field label="Flat below">
            <NumberInput value={f.max_pitch_deg} min={0} step={1} suffix="°" onChange={(v) => setFlat({ max_pitch_deg: v ?? 0 })} />
          </Field>
          <Field label="Mounting">
            <Select value={f.mounting} onChange={(v) => setFlat({ mounting: v })} options={[{ value: "ballast", label: "Ballasted racking" }, { value: "flush", label: "Flush" }]} />
          </Field>
          <Field label="Tilt">
            <NumberInput value={f.tilt_deg} min={0} max={60} step={1} suffix="°" onChange={(v) => setFlat({ tilt_deg: v ?? 0 })} />
          </Field>
          <Field label="Facing (azimuth)" hint="180° = south">
            <NumberInput value={f.azimuth_deg} min={0} max={360} step={1} suffix="°" onChange={(v) => setFlat({ azimuth_deg: v ?? 180 })} />
          </Field>
          <Field label="Row spacing factor" hint="1 = no winter-noon shading">
            <NumberInput value={f.row_spacing_factor} min={0} step={0.05} onChange={(v) => setFlat({ row_spacing_factor: v ?? 1 })} />
          </Field>
          <Field label="Minimum row gap">
            <NumberInput value={f.min_row_gap_m ?? 0.33} min={0} step={0.01} suffix="m" onChange={(v) => setFlat({ min_row_gap_m: v ?? 0 })} />
          </Field>
          <div className="span-3">
            <Switch checked={f.align_to_building ?? true} onChange={(v) => setFlat({ align_to_building: v })} label="Align rows with the building" />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------- battery & financing
function OfferTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const pricing = useDraft(orgId, docs, "pricing");
  const financial = useDraft(orgId, docs, "financial");
  const p = pricing.draft;
  const fin = financial.draft;
  if (!p || !fin) return null;
  const batteries = p.battery_options;
  const setBattery = (i: number, patch: Partial<S["BatteryOption"]>) =>
    pricing.setDraft({ ...p, battery_options: batteries.map((b, j) => (i === j ? { ...b, ...patch } : b)) });
  const f = fin.financing;
  const setFin = (patch: Partial<S["Financing"]>) => financial.setDraft({ ...fin, financing: { ...f, ...patch } });
  return (
    <div className="stack">
      <Section title="Battery options" description={`Offered in the calculator when “Show battery options” is on (Branding). Prices in ${p.currency}.`} footer={pricing.actions(canManage)}>
        <div className="stack">
          {batteries.length === 0 && <p className="muted">No batteries configured.</p>}
          {batteries.map((b, i) => (
            <div key={i} className="form-grid-3" style={{ alignItems: "end", paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
              <Field label="Label">
                <TextInput value={b.label} onChange={(e) => setBattery(i, { label: e.target.value })} />
              </Field>
              <Field label="Model">
                <TextInput value={b.model} onChange={(e) => setBattery(i, { model: e.target.value })} />
              </Field>
              <Field label="Installed price">
                <NumberInput value={b.price} min={0} step={100} suffix={p.currency} onChange={(v) => setBattery(i, { price: v ?? 0 })} />
              </Field>
              <Field label="Energy">
                <NumberInput value={b.energy_kwh} min={0.1} step={0.1} suffix="kWh" onChange={(v) => setBattery(i, { energy_kwh: v ?? b.energy_kwh })} />
              </Field>
              <Field label="Power">
                <NumberInput value={b.power_kw} min={0.1} step={0.1} suffix="kW" onChange={(v) => setBattery(i, { power_kw: v ?? b.power_kw })} />
              </Field>
              <div>
                <Button variant="danger" icon={<Trash2 />} onClick={() => pricing.setDraft({ ...p, battery_options: batteries.filter((_, j) => j !== i) })}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
          <div>
            <Button
              icon={<Plus />}
              onClick={() =>
                pricing.setDraft({
                  ...p,
                  battery_options: [...batteries, { id: `battery-${batteries.length + 1}`, label: "Home battery", model: "", power_kw: 5, energy_kwh: 13.5, price: 12000 }],
                })
              }
            >
              Add battery
            </Button>
          </div>
        </div>
      </Section>
      <Section title="Financing" description="Payments shown next to the cash price when financing is on." footer={financial.actions(canManage)}>
        <div className="stack">
          <Field label="Default option">
            <Select value={f.default_product} onChange={(v) => setFin({ default_product: v })} options={(["cash", "loan", "lease", "ppa"] as const).map((v) => ({ value: v, label: v === "ppa" ? "PPA" : titleCase(v) }))} />
          </Field>
          <h3>Loan</h3>
          <div className="form-grid-3">
            <Field label="APR">
              <NumberInput value={+(f.loan.apr * 100).toFixed(3)} min={0} max={100} step={0.1} suffix="%" onChange={(v) => setFin({ loan: { ...f.loan, apr: (v ?? 0) / 100 } })} />
            </Field>
            <Field label="Term">
              <NumberInput value={f.loan.term_years} min={1} step={1} suffix="years" onChange={(v) => setFin({ loan: { ...f.loan, term_years: Math.max(1, Math.round(v ?? 1)) } })} />
            </Field>
            <Field label="Down payment">
              <NumberInput value={f.loan.down_payment_pct} min={0} max={100} step={1} suffix="%" onChange={(v) => setFin({ loan: { ...f.loan, down_payment_pct: v ?? 0 } })} />
            </Field>
          </div>
          {(["lease", "ppa"] as const).map((k) => (
            <div key={k} className="stack" style={{ gap: 10 }}>
              <h3>{k === "ppa" ? "PPA" : "Lease"}</h3>
              <div className="form-grid-3">
                <Field label="Rate per kWh">
                  <NumberInput value={f[k].rate_per_kwh} min={0} step={0.005} suffix={fin.currency} onChange={(v) => setFin({ [k]: { ...f[k], rate_per_kwh: v ?? 0 } })} />
                </Field>
                <Field label="Term">
                  <NumberInput value={f[k].term_years} min={1} step={1} suffix="years" onChange={(v) => setFin({ [k]: { ...f[k], term_years: Math.max(1, Math.round(v ?? 1)) } })} />
                </Field>
                <Field label="Yearly escalator">
                  <NumberInput value={+(f[k].escalator * 100).toFixed(3)} min={0} max={100} step={0.1} suffix="%" onChange={(v) => setFin({ [k]: { ...f[k], escalator: (v ?? 0) / 100 } })} />
                </Field>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ------------------------------------------------------------ service area
function AreaTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const area = useDraft(orgId, docs, "service_area");
  const a = area.draft;
  if (!a) return null;
  const unrestricted = !a.countries.length && !a.regions.length && !a.area;
  return (
    <div className="stack">
      <Section title="Service area" description="Where you install. Addresses outside are refused before any design credit is used." footer={area.actions(canManage)}>
        <div className="stack">
          {unrestricted && <Alert tone="info">You quote everywhere. Add countries or regions to limit it.</Alert>}
          <div className="form-grid">
            <Field label="Countries" hint="ISO codes, e.g. US, CA">
              <ChipsInput
                values={a.countries}
                onChange={(v) => area.setDraft({ ...a, countries: v })}
                placeholder="US"
                normalize={(v) => v.trim().toUpperCase()}
                validate={(v) => (/^[A-Z]{2}$/.test(v) ? null : `“${v}” is not a two-letter country code`)}
              />
            </Field>
            <Field label="States / provinces" hint="ISO 3166-2, e.g. US-CA, CA-BC">
              <ChipsInput
                values={a.regions}
                onChange={(v) => area.setDraft({ ...a, regions: v })}
                placeholder="US-CA"
                normalize={(v) => v.trim().toUpperCase()}
                validate={(v) => (/^[A-Z]{2}-[A-Z0-9]{1,3}$/.test(v) ? null : `“${v}” is not a region code like US-CA`)}
              />
            </Field>
          </div>
          <Field label="Message outside the area">
            <textarea className="textarea" maxLength={500} value={a.outside_message} onChange={(e) => area.setDraft({ ...a, outside_message: e.target.value })} />
          </Field>
          {a.area && <p className="hint">A custom map area is also set (edit under Advanced).</p>}
        </div>
      </Section>
      <Section
        title="Daily limit"
        description="Protects your credits: at most this many new website designs per day (UTC). Beyond it, visitors see your contact details and you get an email."
        footer={area.actions(canManage)}
      >
        <div className="form-grid" style={{ alignItems: "end" }}>
          <Field label="Max new designs per day">
            <NumberInput
              value={a.limits?.daily_new_designs ?? null}
              disabled={a.limits?.daily_new_designs == null}
              min={0}
              step={1}
              placeholder="Unlimited"
              onChange={(v) => area.setDraft({ ...a, limits: { ...a.limits, daily_new_designs: v == null ? 0 : Math.max(0, Math.round(v)) } })}
            />
          </Field>
          <div style={{ paddingBottom: 8 }}>
            <Switch
              checked={a.limits?.daily_new_designs == null}
              onChange={(unlimited) => area.setDraft({ ...a, limits: { ...a.limits, daily_new_designs: unlimited ? null : 25 } })}
              label="Unlimited"
            />
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------- advanced
function AdvancedTab({ orgId, docs, canManage }: { orgId: string; docs: Docs; canManage: boolean }) {
  const [kind, setKind] = useState<SettingsKind>("pricing");
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const original = JSON.stringify(docs[kind], null, 2);
  const [text, setText] = useState(original);
  useEffect(() => setText(original), [original]);
  let parseError: string | null = null;
  try {
    JSON.parse(text);
  } catch (e) {
    parseError = (e as Error).message;
  }
  return (
    <Card
      title="Advanced (JSON)"
      description="Every setting, as the engine reads it. Saved documents are validated against the schema."
      actions={
        <div style={{ width: 200 }}>
          <Select value={kind} onChange={setKind} options={KINDS.map((k) => ({ value: k, label: titleCase(k) }))} />
        </div>
      }
      footer={
        <>
          <Button size="sm" variant="ghost" icon={<RotateCcw />} disabled={text === original} onClick={() => setText(original)}>
            Discard
          </Button>
          <Button
            size="sm"
            variant="primary"
            icon={<Save />}
            disabled={!canManage || text === original || !!parseError}
            loading={save.isPending}
            onClick={() =>
              save.mutate({ kind, document: JSON.parse(text) }, { onSuccess: () => toast(`${titleCase(kind)} saved`), onError: (e) => toast(errorMessage(e), true) })
            }
          >
            Save {titleCase(kind)}
          </Button>
        </>
      }
    >
      <div className="stack">
        <textarea className={cx("textarea", "code")} spellCheck={false} value={text} onChange={(e) => setText(e.target.value)} />
        {parseError && <span className="field-error">{parseError}</span>}
      </div>
    </Card>
  );
}

export function Rules() {
  const orgId = useOrgId();
  const { canManage } = useOrg();
  const settings = useEffectiveSettings(orgId);
  const [tab, setTab] = useState<Tab>("roof");
  if (settings.isLoading) return <Spinner />;
  if (settings.error || !settings.data) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;
  const docs = settings.data.documents;
  const tabs: { id: Tab; label: string }[] = [
    { id: "roof", label: "Roof & setbacks" },
    { id: "module", label: "Modules & layout" },
    { id: "offer", label: "Battery & financing" },
    { id: "area", label: "Service area" },
    { id: "advanced", label: "Advanced" },
  ];
  const props = { orgId, docs, canManage };
  return (
    <>
      <PageHead
        title="Rules"
        description="How your designs are drawn and priced. Changes apply to new designs and quotes; existing ones keep the rules they were made with."
        actions={settings.data.pins ? <span className="badge plain" title="Settings preset">{Object.values(settings.data.pins)[0]?.ref.split(" ")[0]}</span> : null}
      />
      {!canManage && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">Only owners and admins can change the rules.</Alert>
        </div>
      )}
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} className={cx(tab === t.id && "on")} onClick={() => setTab(t.id)} role="tab" aria-selected={tab === t.id}>
            {t.id === "advanced" && <Braces size={13} style={{ verticalAlign: -2, marginRight: 4 }} />}
            {t.label}
          </button>
        ))}
      </div>
      {tab === "roof" && <RoofTab {...props} />}
      {tab === "module" && <ModuleTab {...props} />}
      {tab === "offer" && <OfferTab {...props} />}
      {tab === "area" && <AreaTab {...props} />}
      {tab === "advanced" && <AdvancedTab {...props} />}
    </>
  );
}
